/**
 * Kickard PHASE 4.0.4 — Ranked Lobby (Phase 5.0B premium V1 presentation).
 *
 * Feature-flagged: renders only when /api/ranked/availability reports
 * `enabled && activeSeasonExists`. Hidden otherwise (per PRD).
 * Never shows hiddenMMR. Rank band (floor / next threshold) comes only
 * from the server's `rank.band` — no thresholds are computed here.
 * Header/tabs reuse the Season Table V7 chrome (RankedChrome).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGuest } from '@/lib/guestIdentity';
import { getRankedAvailability, getMyRankedState } from '@/api/rankedClient';
import { RankedBackdrop, RankedHeader, RankedTabs } from '../components/RankedChrome';
import { TIER_ART, crestNumeralStyle, CREST_PLACEHOLDER_STYLE } from '../lib/rankCrest';

const R = '/kickard/ranked';
const SUBTITLE = 'COMPETE. CLIMB. PROVE YOURSELF.';

// Home-parity rank crest map (duplicated verbatim from HomeScreen, same
// convention as MatchResultPremium / Scoreboard / RankedLeaderboardScreen).
const DIVISION_ROMAN = { 1: 'I', 2: 'II', 3: 'III' };

export function rankedTitle(seasonName) {
  return seasonName ? (
    <>RANKED <span className="kst__title-accent">{String(seasonName).toUpperCase()}</span></>
  ) : 'RANKED SEASON';
}

// Pure display math over the server-provided band.
export function bandProgress(sr, band) {
  if (!band || band.isMax || !band.next) return null;
  const span = band.next.minSR - band.minSR;
  const progress = span > 0 ? Math.max(0, Math.min(1, (sr - band.minSR) / span)) : 0;
  return { srToNext: Math.max(0, band.next.minSR - sr), progress };
}

function Crest({ tier, division, className, testid }) {
  const known = tier && TIER_ART[tier];
  const numeral = known && tier !== 'legend' && division ? DIVISION_ROMAN[Number(division)] : null;
  return (
    <div className={className} data-testid={testid} data-tier={known ? tier : 'none'}>
      <img src={known ? TIER_ART[tier] : TIER_ART.gold} alt="" aria-hidden style={known ? undefined : CREST_PLACEHOLDER_STYLE} />
      {numeral && <span className={className === 'krk-crest' ? 'krk-crest__numeral' : undefined} aria-hidden style={crestNumeralStyle(tier)}>{numeral}</span>}
    </div>
  );
}

function Bar({ pct, end, labels, testid }) {
  return (
    <>
      <div className="krk-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} data-testid={testid}>
        <div className="krk-bar__fill" style={{ width: `${pct}%` }} data-testid={`${testid}-fill`} />
        {end && <span className="krk-bar__end" aria-hidden />}
      </div>
      <div className="krk-bar-labels">{labels.map((l, i) => <span key={i}>{l}</span>)}</div>
    </>
  );
}

export function RankPanel({ me }) {
  const p = me.placement || {};
  const r = me.rank || {};
  const sr = r.currentSR || 0;
  if (!p.placementComplete) {
    const played = p.placementMatchesPlayed || 0;
    const req = p.placementMatchesRequired || 10;
    return (
      <section className="krk-panel krk-rank" data-testid="ranked-lobby-rank-card" data-state="placement">
        <div className="krk-label">Placement</div>
        <div className="krk-rank__name" data-testid="ranked-placement-count">{played} / {req}</div>
        <div className="krk-rank__sr" data-testid="ranked-placement-wins">Wins: {p.placementWins || 0}</div>
        <Bar pct={req > 0 ? Math.min(100, (played / req) * 100) : 0} labels={['0', 'Placement matches', String(req)]} testid="ranked-placement-bar" />
      </section>
    );
  }
  const band = r.band || null;
  const prog = bandProgress(sr, band);
  return (
    <section className="krk-panel krk-rank" data-testid="ranked-lobby-rank-card" data-state={band?.isMax ? 'max' : 'ranked'}>
      <div className="krk-rank__top">
        <div className="krk-rank__cur">
          <div className="krk-label">Current Rank</div>
          <div className="krk-rank__name" data-testid="ranked-current-label">{r.currentLabel || 'Unranked'}</div>
          <div className="krk-rank__sr" data-testid="ranked-current-sr">{sr} SR</div>
        </div>
        {prog && (
          <div className="krk-rank__next" data-testid="ranked-next">
            <Crest tier={band.next.tier} division={band.next.division} className="krk-next-crest" testid="ranked-next-crest" />
            <div className="krk-next__text">
              <div className="krk-label">Next Rank</div>
              <div className="krk-next__name" data-testid="ranked-next-label">{band.next.label}</div>
              <div className="krk-next__sr" data-testid="ranked-next-threshold">{band.next.minSR} SR</div>
            </div>
          </div>
        )}
        {band?.isMax && (
          <div className="krk-rank__next" data-testid="ranked-max">
            <div className="krk-max">Max Rank</div>
          </div>
        )}
      </div>
      {prog && (
        <Bar
          pct={prog.progress * 100}
          end
          labels={[String(band.minSR), `${prog.srToNext} SR to next rank`, String(band.next.minSR)]}
          testid="ranked-band-bar"
        />
      )}
      {band?.isMax && <Bar pct={100} labels={[String(band.minSR), 'Highest rank', '']} testid="ranked-band-bar" />}
    </section>
  );
}

export function RankedStats({ rec }) {
  const cells = [
    ['crossed-swords', 'Ranked matches', `${rec.rankedMatchesPlayed || 0}`, 'ranked-stat-matches'],
    ['trophy', 'Wins / Losses', `${rec.wins || 0} / ${rec.losses || 0}`, 'ranked-stat-wl'],
    ['target', 'Penalty W / L', `${rec.penaltyWins || 0} / ${rec.penaltyLosses || 0}`, 'ranked-stat-pen'],
  ];
  return (
    <section className="krk-panel krk-stats" data-testid="ranked-lobby-record">
      {cells.map(([icon, label, value, tid]) => (
        <div className="krk-stat" key={tid}>
          <img src={`${R}/${icon}.svg`} alt="" aria-hidden style={{ filter: 'invert(74%) sepia(70%) saturate(1200%) hue-rotate(358deg) brightness(102%)' }} />
          <div className="krk-stat__label">{label}</div>
          <div className="krk-stat__value" data-testid={tid}>{value}</div>
        </div>
      ))}
    </section>
  );
}

export function PlayRankedButton({ onPlay }) {
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const onClick = () => {
    if (lock.current) return; // one tap = one search
    lock.current = true;
    setBusy(true);
    onPlay();
  };
  return (
    <button type="button" onClick={onClick} disabled={busy} aria-busy={busy} className="krk-play" data-testid="ranked-lobby-start-btn">
      PLAY RANKED
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m9 5 7 7-7 7" /></svg>
    </button>
  );
}

export function RankedLobbyView({ seasonName, me, onBack, onNavigate, onPlay }) {
  const r = me.rank || {};
  const earned = !!me.placement?.placementComplete;
  return (
    <Shell seasonName={seasonName} onBack={onBack} onNavigate={onNavigate} testid="ranked-lobby">
      <div className="krk-hero" data-testid="ranked-hero">
        <div className="krk-pedestal" aria-hidden />
        <Crest
          tier={earned ? String(r.currentTier || '').toLowerCase() : null}
          division={earned ? r.currentDivision : null}
          className="krk-crest"
          testid="ranked-hero-crest"
        />
      </div>
      <RankPanel me={me} />
      <RankedStats rec={me.record || {}} />
      <PlayRankedButton onPlay={onPlay} />
      <p className="krk-note">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M12 10.5v6" /><circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" /></svg>
        No direct rematch in Ranked.
      </p>
    </Shell>
  );
}

function Shell({ seasonName, onBack, onNavigate, testid, children }) {
  return (
    <RankedBackdrop testid={testid}>
      <div className="krk-stadium" aria-hidden />
      <div className="kst__inner krk" style={{ '--krk-stadium': `url(${R}/ranked_stadium.webp)`, '--krk-pedestal': `url(${R}/rank_pedestal.webp)` }}>
        <RankedHeader
          title={rankedTitle(seasonName)}
          subtitle={SUBTITLE}
          onBack={onBack}
          backTestId={testid === 'ranked-lobby' ? 'ranked-lobby-back' : 'ranked-unavailable-back'}
          titleTestId="ranked-title"
        />
        <RankedTabs active="ranked" onNavigate={onNavigate} />
        {children}
      </div>
    </RankedBackdrop>
  );
}

function StateFrame({ seasonName, msg, onBack, onNavigate, testid }) {
  return (
    <Shell seasonName={seasonName} onBack={onBack} onNavigate={onNavigate} testid={testid}>
      <div className="krk-panel krk-state">
        <p className="krk-state__msg">{msg}</p>
      </div>
    </Shell>
  );
}

export default function RankedLobbyScreen() {
  const navigate = useNavigate();
  const [avail, setAvail] = useState(null);
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const a = await getRankedAvailability();
      if (cancelled) return;
      setAvail(a);
      if (a.ok && a.enabled && a.activeSeasonExists) {
        const g = loadGuest();
        if (g?.guestId) {
          const m = await getMyRankedState(g.guestId);
          if (!cancelled) setMe(m);
        } else {
          setErr('no_guest');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onBack = () => navigate(-1);
  const onNavigate = (path) => navigate(path);
  const frame = (msg, testid, seasonName) => (
    <StateFrame seasonName={seasonName} msg={msg} onBack={onBack} onNavigate={onNavigate} testid={testid} />
  );

  if (avail === null) return frame('Loading…', 'ranked-loading');

  // PHASE 5.0B — only disable Ranked when the server AUTHORITATIVELY
  // reports it. A network error (!avail.ok) MUST NOT masquerade as
  // "Ranked unavailable". Show an error+retry instead.
  if (!avail.ok) return frame("Couldn't check Ranked availability.", 'ranked-availability-error');
  // Explicit CLOSED only.
  if (!avail.enabled) return frame('Ranked is currently unavailable.', 'ranked-disabled');
  if (!avail.activeSeasonExists) return frame('No active season.', 'ranked-no-season');
  const seasonName = avail.activeSeason?.name;
  if (err === 'no_guest') return frame('Please set a display name first.', 'ranked-no-guest', seasonName);
  if (!me) return frame('Loading rank…', 'ranked-loading-rank', seasonName);
  if (!me.ok) return frame("Couldn't load your rank.", 'ranked-error', seasonName);

  return (
    <RankedLobbyView
      seasonName={me.season?.name || seasonName}
      me={me}
      onBack={onBack}
      onNavigate={onNavigate}
      onPlay={() => navigate('/ranked/search')}
    />
  );
}
